<template>
    <BaseCard padding="md" class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div class="flex items-center gap-3 min-w-0 flex-1">
            <ReflectionIcon :kind="iconKind" size="md" />
            <div class="flex flex-col gap-1 min-w-0">
                <div class="flex items-center gap-2.5 flex-wrap">
                    <span class="body-semibold text-ink">{{ item.title }}</span>
                    <FeatureChip :feature="chipFeature">{{ item.chipLabel }}</FeatureChip>
                </div>
                <div v-if="item.subtitle" class="scaffold-body-small text-ink-muted">{{ item.subtitle }}</div>
                <div v-if="closesOn" class="scaffold-details-small text-ink-muted">Closes on {{ closesOn }}</div>
                <div v-if="item.progress" class="mt-2 flex flex-col gap-2 max-w-40">
                    <div class="h-1 w-full bg-surface-sunken rounded-full overflow-hidden">
                        <div class="h-full bg-progress-fill rounded-full transition-[width]" :style="{ width: `${progressPct}%` }"></div>
                    </div>
                    <div class="scaffold-details-small text-ink-muted">{{ item.progress.responded }} of {{ item.progress.total }} responses recorded</div>
                </div>
            </div>
        </div>
        <a
            v-if="isInlineActivatable"
            :href="item.href"
            class="base-btn-neutral base-btn-sm sm:shrink-0 whitespace-nowrap text-center self-start sm:self-auto"
            :aria-label="`${item.ctaLabel} ${item.title}`"
            @click.prevent="emit('activate', item)">
            {{ item.ctaLabel }}
        </a>
        <a
            v-else-if="item.href"
            :href="item.href"
            class="base-btn-neutral base-btn-sm sm:shrink-0 whitespace-nowrap text-center self-start sm:self-auto"
            :aria-label="`${item.ctaLabel} ${item.title}`">
            {{ item.ctaLabel }}
        </a>
    </BaseCard>
</template>

<script setup lang="ts">
import BaseCard from "~vue/components/BaseCard.vue";
import FeatureChip, { type FeatureChipFeature } from "~vue/components/FeatureChip.vue";
import ReflectionIcon, { type ReflectionIconKind } from "~vue/pages/reflections/ReflectionIcon.vue";
import type { UpcomingItem } from "~vue/pages/reflections/UpcomingTab.vue";
import { formatDate } from "~vue/utils/threeSixty";
import { computed } from "vue";

interface Props {
    item: UpcomingItem;
}

const { item } = defineProps<Props>();
const emit = defineEmits<{ (event: "activate", item: UpcomingItem): void }>();

// The group caption above the row carries the relative deadline ("Closes in 3
// days"), so the row spells out the date itself rather than repeating it.
const closesOn = computed(() => formatDate(item.closesAt, "long"));

// Row kinds that swap the hub content pane inline (rather than navigating
// away): self-reflection cycles/campaigns → take flow; active-360 status →
// 360s tab; DRAFT 360 setup or 360 campaign → Nominate panel. Everything
// else keeps its plain <a> so middle-click / right-click still open in a
// new tab.
const isInlineActivatable = computed(() => {
    if (item.kind === "team_reflection" && item.inviteToken) {
        return true;
    }
    if (item.kind === "self_reflection" && item.cycleId) {
        return true;
    }
    if (item.kind === "self_reflection_campaign" && item.campaignId) {
        return true;
    }
    if (item.kind === "active_360_status") {
        return true;
    }
    if (item.kind === "cycle_setup" && item.cycleId) {
        return true;
    }
    if (item.kind === "cycle_setup_campaign" && item.campaignId) {
        return true;
    }
    return false;
});

const iconKind = computed<ReflectionIconKind>(() => {
    switch (item.kind) {
        case "team_reflection":
            return "team";
        case "self_reflection":
        case "self_reflection_campaign":
            return "self";
        case "active_360_status":
        case "cycle_setup":
        case "cycle_setup_campaign":
            return "sixty";
        default:
            return "upcoming";
    }
});

const chipFeature = computed<FeatureChipFeature>(() => {
    switch (item.kind) {
        case "team_reflection":
            return "team-reflect";
        case "self_reflection":
        case "self_reflection_campaign":
            return "self-reflect";
        case "cycle_setup":
        case "cycle_setup_campaign":
        case "active_360_status":
            return "three-sixty";
        default:
            return "three-sixty";
    }
});

const progressPct = computed(() => {
    if (!item.progress || !item.progress.total) {
        return 0;
    }
    return Math.min(100, Math.round((item.progress.responded / item.progress.total) * 100));
});
</script>
